import { microwave, youtube } from '@round/api';
import useAbortableEffect from 'Hooks/useAbortableEffect';
import uniq from 'lodash/uniq';
import { isNumber } from 'utility/utility';
import { useMicrowaveInfluencerAccounts } from '../hooks/useMicrowaveInfluencerAccounts';
import { useTiktokUserImages } from 'Modules/Influencer/hooks/useTiktokUserImages';
import useInstagramUserImages from 'Modules/Instagram/hooks/useInstagramUserImages';
import { useMicrowavePaymentRequests } from '../hooks/useMicrowavePaymentRequests';
import { useCallback, useMemo, useState } from 'react';
import { useReleases } from 'Modules/Advertising/Projects/hooks/useReleases';
import { showNotification } from 'helpers';

export default function useOutstandingPayments() {
    const [status, setStatus] = useState<'not-initialized' | 'initialized' | 'error'>('not-initialized');
    const { fetchData: fetchPaymentRequests, ...paymentRequests } = useMicrowavePaymentRequests();

    const fetchData = useCallback(
        async (params: Omit<microwave.GetPaymentRequestsParams, 'include_totals'>, requestInit?: RequestInit) => {
            try {
                const response = await fetchPaymentRequests({ ...params, include_totals: true }, requestInit);

                if (response.status === 404) {
                    setStatus('error');
                }
                return response;
            } catch (e) {
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }

                setStatus('error');
                throw e;
            }
        },
        [fetchPaymentRequests]
    );

    const accountIds = uniq(
        paymentRequests.data?.paymentRequests.map((p) => p.microwave_influencer_account_id).filter(isNumber)
    );

    const isInfluencerAccountsFetchEnabled = !!accountIds.length && paymentRequests.status === 'success';
    const { state: influencerAccountFetchState, reset: resetInfluencerAccounts } = useMicrowaveInfluencerAccounts(
        {
            id: accountIds.join(','),
            page: 1,
            page_size: accountIds.length,
        },
        { enabled: isInfluencerAccountsFetchEnabled }
    );

    useAbortableEffect(() => {
        if (paymentRequests.status !== 'success') {
            return;
        }

        if (!accountIds.length) {
            setStatus('initialized');
        }
    }, [paymentRequests.status, accountIds.length]);

    const { fetchData: fetchTiktokImages, ...tiktokUserImages } = useTiktokUserImages();
    const { fetchData: fetchInstagramImages, ...instagramUserImages } = useInstagramUserImages();
    const [youtubeChannels, setYoutubeChannels] = useState<youtube.Channel[]>([]);

    const imageSourceData = useMemo(() => {
        const accounts = influencerAccountFetchState.data ?? [];
        return {
            tiktok: accounts.filter((a) => a.platform === 'tiktok').map((a) => a.account_id),
            instagram: accounts.filter((a) => a.platform === 'instagram').map((a) => a.account_id),
            youtube: accounts.filter((a) => a.platform === 'youtube').map((a) => a.account_id),
        };
    }, [influencerAccountFetchState.data]);

    useAbortableEffect(
        (signal) => {
            if (influencerAccountFetchState.status !== 'success') {
                return;
            }

            const fetchImages = async () => {
                try {
                    const tiktokImagesToFetch = imageSourceData.tiktok.filter(
                        (id) => !tiktokUserImages.data?.find((img) => img.user_id === id)
                    );
                    const instagramImagesToFetch = imageSourceData.instagram.filter(
                        (id) => !instagramUserImages.data?.images?.find((img) => img.user_id === id)
                    );
                    const youtubeIdsToFetch = imageSourceData.youtube.filter(
                        (id) => !youtubeChannels.find((channel) => channel.id === id)
                    );

                    await Promise.allSettled([
                        instagramImagesToFetch.length ? fetchInstagramImages(instagramImagesToFetch, { signal }) : null,
                        tiktokImagesToFetch.length ? fetchTiktokImages(tiktokImagesToFetch, { signal }) : null,
                        youtubeIdsToFetch.length
                            ? youtube
                                  .getChannels({
                                      id: youtubeIdsToFetch.join(','),
                                      page_size: youtubeIdsToFetch.length,
                                  })
                                  .then((res) =>
                                      res.status === 200
                                          ? setYoutubeChannels((prev) => prev.concat(res.data.results))
                                          : Promise.resolve()
                                  )
                            : Promise.resolve(),
                    ]);
                } finally {
                    setStatus('initialized');
                }
            };

            void fetchImages();
        },
        [
            imageSourceData,
            fetchTiktokImages,
            tiktokUserImages.data,
            fetchInstagramImages,
            instagramUserImages.data,
            youtubeChannels,
            influencerAccountFetchState.status,
        ]
    );

    const { fetchData: fetchReleases, ...releases } = useReleases();

    const releaseIds = useMemo(
        () => uniq(paymentRequests.data?.paymentRequests.map((p) => p.release_id).filter(isNumber)),
        [paymentRequests.data]
    );

    useAbortableEffect(
        (signal) => {
            async function fetchReleaseData(ids: number[]) {
                try {
                    await fetchReleases({ id: ids.join(',') }, { signal });
                } catch (e) {
                    if (e instanceof Error && e.name === 'AbortError') {
                        return;
                    }
                    showNotification('Could not fetch releases', 'error');
                }
            }

            const releaseIdsToFetch = releaseIds.filter((id) => !releases.data?.releases.find((r) => r.id === id));

            if (paymentRequests.status !== 'success' || !releaseIdsToFetch.length) {
                return;
            }

            void fetchReleaseData(releaseIdsToFetch);
        },
        [fetchReleases, releases.data, paymentRequests.status, releaseIds]
    );

    const reset = () => {
        paymentRequests.reset();
        resetInfluencerAccounts();
        setStatus('not-initialized');
    };

    const rows = (paymentRequests.data?.paymentRequests ?? []).map((paymentRequest) => {
        const release = releases.data?.releases.find((r) => r.id === paymentRequest.release_id);
        const influencerAccount = influencerAccountFetchState.data?.find(
            (a) => a.id === paymentRequest.microwave_influencer_account_id
        );

        const images = {
            tiktok: (() => {
                const image = tiktokUserImages.data?.find((img) => img.user_id === influencerAccount?.account_id);
                return image?.avatar_thumb.cached_url || image?.avatar_thumb.original_url;
            })(),
            instagram: (() => {
                const image = instagramUserImages.data?.images?.find(
                    (img) => img.user_id === influencerAccount?.account_id
                );
                return image?.avatar_thumb.cached_url || image?.avatar_thumb.original_url;
            })(),
            youtube:
                youtubeChannels.find((channel) => channel.id === influencerAccount?.account_id)?.thumbnail || undefined,
        };

        const userImageUrl = images[paymentRequest.platform];
        return {
            ...paymentRequest,
            influencerAccount,
            release,
            userImageUrl,
        };
    });

    return {
        rows,
        totals: paymentRequests.data?.totals ?? null,
        count: paymentRequests.data?.count ?? 0,
        status,

        fetchData,
        updatePayment: paymentRequests.update,
        makePaypalPayment: paymentRequests.makePaypalPayment,
        markAsPaidManually: paymentRequests.markAsPaidManually,
        makeVenmoPayment: paymentRequests.makeVenmoPayment,
        reset,
    };
}
